import { Extension } from '@tiptap/core';
import type { Editor, JSONContent } from '@tiptap/core';
import { activateMediaManager } from 'Shared/FrameModules/MediaLibrary/media_manager_helpers';
import { wikiImageSizes } from './wiki_image';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      mediaManager: {
         activateMediaManager: () => ReturnType;
      };
   }
}

type MediaItem = Awaited<ReturnType<typeof activateMediaManager>>;

// MediaItemData exposes each field as a getter function, and only for the
// fields the item actually has.
function field(mediaItem: MediaItem, name: string): unknown {
   const getter = mediaItem.data?.[name];
   return typeof getter === 'function' ? getter.call(mediaItem.data) : undefined;
}

/**
 * The picked item as an image node, with a picture for each size it has a
 * URL for, or null for media the editor can't show as a picture.
 */
function imageContent(mediaItem: MediaItem): JSONContent | null {
   const imageid = field(mediaItem, 'imageid');
   if (!imageid || !mediaItem.toWikiText().startsWith('[image|')) {
      return null;
   }
   const renditions: Record<string, { src: string }> = {};
   for (const size of wikiImageSizes) {
      const src = field(mediaItem, size);
      if (typeof src === 'string') {
         renditions[size] = { src };
      }
   }
   if (!renditions.original) {
      return null;
   }
   return {
      type: 'paragraph',
      content: [{ type: 'wikiImage', attrs: { imageid, renditions } }],
   };
}

/**
 * An image node goes in as its own paragraph, so only where a paragraph
 * can stand alone. Tables and code blocks are source text and headings
 * plain text, which a paragraph would split. A second paragraph in a list
 * item saves as its own bullet, which the image then leaves empty on the
 * page. All of those get the wikitext instead.
 */
function canHoldImage(editor: Editor): boolean {
   const { $from } = editor.state.selection;
   return (
      Boolean(editor.schema.nodes.wikiImage) &&
      $from.parent.type.name === 'paragraph' &&
      $from.node(-1).type.name !== 'listItem'
   );
}

export const MediaManager = Extension.create({
   name: 'mediaManager',

   addCommands() {
      return {
         activateMediaManager:
            () =>
            ({ commands }) => {
               commands.blur();
               const editor = this.editor;

               // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
               activateMediaManager('wiki_media').then(mediaItem => {
                  const image = canHoldImage(editor) ? imageContent(mediaItem) : null;
                  editor
                     .chain()
                     .focus()
                     .insertContent(image ?? mediaItem.toWikiText())
                     .run();
               });
               return true;
            },
      };
   },
});
